import React, { useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import { IconSvg } from 'components/atoms';

import NestedMenuItem from './NestedMenuItem';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0.5, 1.375),
    flexWrap: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  },
  select: {
    color: 'white',
    '&:hover': {
      borderBottom: 'none !important',
    },
    '&::before': {
      borderBottom: 'none !important',
    },
    '&::after': {
      borderBottom: 'none !important',
    },
  },
  selectRoot: {
    fontFamily: 'Roboto',
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1),
  },
  selectOutlined: {
    border: 'none',
    '&:hover': {
      border: 'none',
    },
  },
  icon: {
    color: 'white',
    fontSize: theme.spacing(5),
    marginLeft: -theme.spacing(0.5),
  },
  selectIcon: {
    color: 'white',
  },
  selectPresent: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: theme.spacing(1 / 2),
  },
}));

const FilterSelect = props => {
  const {
    className,
    items,
    placeholder,
    startText,
    name,
    icon,
    iconProps,
    onChange,
    value,
    ...others
  } = props;

  const classes = useStyles();
  const [selected, setSelected] = React.useState(value);

  const handleChange = event => {
    setSelected(event.target.value);
    if (onChange) onChange(event);
  };

  const [menuPosition, setMenuPosition] = React.useState(null);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const handleRightClick = event => {
    if (menuPosition) {
      return;
    }
    event.preventDefault();
    setMenuPosition({
      top: event.pageY,
      left: event.pageX,
    });
  };

  const handleItemClick = event => {
    handleChange(event);
    setMenuPosition(null);
  };

  const hasValueSelected = () => {
    return Array.isArray(selected) ? selected.length : selected;
  };

  return (
    <Grid
      className={clsx(classes.root, className, {
        [classes.selectPresent]: hasValueSelected(),
      })}
      container
      spacing={0}
      alignItems="flex-start"
    >
      <Grid item>
        <IconSvg
          name={icon}
          viewBox="0 0 1080 1080"
          fontSize="large"
          className={classes.icon}
          {...iconProps}
        />
      </Grid>
      <Grid item>
        <Select
          {...others}
          open={Boolean(menuPosition)}
          name={name}
          displayEmpty
          variant="standard"
          className={classes.select}
          onOpen={handleRightClick}
          MenuProps={{
            open: !!menuPosition,
            onClose: () => setMenuPosition(null),
            // onSelect: () => setMenuPosition(null),
            anchorPosition: menuPosition,
          }}
          value={selected}
          // onChange={handleChange}
          renderValue={() => {
            if (selected?.length === 0 || !selected) {
              return <em>{placeholder}</em>;
            }

            const getSelectedItems = arrayItems => {
              return arrayItems.reduce((acc, curr) => {
                return [
                  ...acc,
                  ...(curr.children
                    ? getSelectedItems(curr.children)
                    : Array.isArray(selected)
                    ? selected.includes(curr.value)
                      ? [curr]
                      : [undefined]
                    : curr.value === selected
                    ? [curr]
                    : [undefined]),
                ].filter(x => x);
              }, []);
            };

            const selectedItems = getSelectedItems(items);

            return (
              (startText || '') +
              selectedItems.map(i => i.description).join(', ')
            );
          }}
          classes={{
            root: classes.selectRoot,
            outlined: classes.selectOutlined,
            icon: classes.selectIcon,
          }}
        >
          <MenuItem disabled value="">
            <em>{placeholder}</em>
          </MenuItem>

          {items?.map(item => {
            if (item.children && item.children.length)
              return (
                <NestedMenuItem
                  key={item.value}
                  label={item.description}
                  parentMenuOpen={!!menuPosition}
                >
                  {item.children?.map(subItem => (
                    <MenuItem
                      key={subItem.value}
                      value={subItem.value}
                      onClick={e =>
                        handleItemClick({
                          ...e,
                          target: { ...e.target, value: subItem.value },
                        })
                      }
                    >
                      {subItem.description}
                    </MenuItem>
                  ))}
                </NestedMenuItem>
              );
            else
              return (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  onClick={e =>
                    handleItemClick({
                      ...e,
                      target: { ...e.target, value: item.value },
                    })
                  }
                >
                  {item.description}
                </MenuItem>
              );
          })}
        </Select>
      </Grid>
    </Grid>
  );
};

const FilterSelectItemShape = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  description: PropTypes.string.isRequired,
});

FilterSelectItemShape.children = PropTypes.arrayOf(FilterSelectItemShape);

FilterSelect.propTypes = {
  ...Select.propTypes,
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Placeholder
   */
  placeholder: PropTypes.string,
  /**
   * Text to show before the selected value on input
   */
  startText: PropTypes.string,
  /**
   * Item source data as shaped value | description
   */
  items: PropTypes.arrayOf(FilterSelectItemShape).isRequired,
};

export default FilterSelect;
